import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { pathMetricsForViewport } from "@/lib/genieWaypoints";

gsap.registerPlugin(ScrollTrigger);

const PATH_STYLE = {
    stroke: "#FDB927",
    strokeLinecap: "round",
    fill: "none",
};

const TRACK_STYLE = {
    stroke: "rgba(6, 78, 87, 0.14)",
    strokeLinecap: "round",
    fill: "none",
};

const STEPS = [
    {
        num: "01",
        title: "Tell Us Your Wish",
        text: "Share what you want to renovate — a single room, a kitchen, or the whole home. Big or small, every wish counts.",
    },
    {
        num: "02",
        title: "Discover Your Solution",
        text: "Explore the right services, professionals and ideas matched to your space, style and budget.",
    },
    {
        num: "03",
        title: "Plan & Book",
        text: "Choose your services, review a clear plan with transparent pricing, and schedule the work in a few clicks.",
    },
    {
        num: "04",
        title: "Watch It Come Together",
        text: "Track progress as your renovation moves from idea to reality. Wish granted — exactly as imagined.",
    },
];

const SPARKLE_PATH = "M12 1.5 14.6 9.4 22.5 12 14.6 14.6 12 22.5 9.4 14.6 1.5 12 9.4 9.4Z";

const markerPoint = (section, marker) => {
    const sectionRect = section.getBoundingClientRect();
    const markerRect = marker.getBoundingClientRect();
    return {
        x: markerRect.left + markerRect.width / 2 - sectionRect.left,
        y: markerRect.top + markerRect.height / 2 - sectionRect.top,
    };
};

const stepAnchorPoint = (section, stepEl, x) => {
    const sectionRect = section.getBoundingClientRect();
    const stepRect = stepEl.getBoundingClientRect();
    return {
        x,
        y: stepRect.top + 56 - sectionRect.top,
    };
};

const buildJourneyPath = (section, anchors) => {
    if (anchors.length < 2) return null;

    const points = anchors.map((anchor) =>
        typeof anchor.getBoundingClientRect === "function"
            ? markerPoint(section, anchor)
            : anchor,
    );
    const first = points[0];
    const startX = Math.max(20, first.x - 52);
    const startY = Math.max(16, first.y - 64);

    let d = `M ${startX} ${startY}`;
    points.forEach((point, index) => {
        if (index === 0) {
            d += ` Q ${startX + 28} ${(startY + point.y) / 2} ${point.x} ${point.y}`;
            return;
        }
        const prev = points[index - 1];
        const midY = (prev.y + point.y) / 2;
        d += ` C ${prev.x} ${midY}, ${point.x} ${midY}, ${point.x} ${point.y}`;
    });

    return d;
};

const buildMobilePath = (section, steps, x) => {
    if (steps.length < 2) return null;

    const points = steps.map((step) => stepAnchorPoint(section, step, x));
    let d = `M ${points[0].x} ${Math.max(12, points[0].y - 40)}`;
    points.forEach((point, index) => {
        if (index === 0) {
            d += ` L ${point.x} ${point.y}`;
            return;
        }
        d += ` L ${point.x} ${point.y}`;
    });
    return d;
};

const pathProgressForPoint = (pathEl, point) => {
    const length = pathEl.getTotalLength();
    let best = 0;
    let bestDist = Infinity;

    for (let i = 0; i <= 80; i += 1) {
        const at = (i / 80) * length;
        const sample = pathEl.getPointAtLength(at);
        const dist = (sample.x - point.x) ** 2 + (sample.y - point.y) ** 2;
        if (dist < bestDist) {
            bestDist = dist;
            best = i / 80;
        }
    }

    return best;
};

const triggerSparkle = (marker) => {
    const sparkle = marker.querySelector("[data-step-sparkle]");
    if (!sparkle) return;

    gsap.killTweensOf(sparkle);
    gsap.set(sparkle, { opacity: 0, scale: 0.55, rotate: 0, transformOrigin: "center center" });
    gsap.to(sparkle, {
        opacity: 0.88,
        scale: 1,
        rotate: 16,
        duration: 0.3,
        ease: "power2.out",
        onComplete: () => {
            gsap.to(sparkle, { opacity: 0, duration: 0.42, ease: "power1.in" });
        },
    });
};

const applyMarkerState = (marker, state) => {
    const dot = marker.querySelector("[data-step-dot]");
    if (!dot) return;

    if (state === "upcoming") {
        gsap.set(dot, {
            backgroundColor: "rgba(6, 78, 87, 0.16)",
            boxShadow: "none",
            scale: 1,
        });
        return;
    }

    if (state === "current") {
        gsap.set(dot, {
            backgroundColor: "#FDB927",
            boxShadow: "0 0 10px rgba(253, 185, 39, 0.42)",
            scale: 1.12,
        });
        return;
    }

    gsap.set(dot, {
        backgroundColor: "#FDB927",
        boxShadow: "none",
        scale: 1,
    });
};

const applyStepEmphasis = (stepEl, index, activeIndex) => {
    if (index === activeIndex) {
        gsap.set(stepEl, { opacity: 1 });
        return;
    }
    gsap.set(stepEl, { opacity: index < activeIndex ? 0.94 : 0.82 });
};

export const HowItWorks = () => {
    const sectionRef = useRef(null);
    const headingRef = useRef(null);
    const stepsColRef = useRef(null);
    const trackRef = useRef(null);
    const progressRef = useRef(null);
    const glowRef = useRef(null);
    const mobileTrackRef = useRef(null);
    const mobileProgressRef = useRef(null);
    const thresholdsRef = useRef([]);
    const lastActiveRef = useRef(-1);
    const sparkledRef = useRef(new Set());
    const progressTriggerRef = useRef(null);

    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const section = sectionRef.current;
        const heading = headingRef.current;
        const stepsCol = stepsColRef.current;
        if (!section || !heading || !stepsCol) return undefined;

        const steps = gsap.utils.toArray("[data-process-step]", stepsCol);
        const markers = gsap.utils.toArray("[data-step-marker]", stepsCol);
        const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        const metrics = pathMetricsForViewport();

        const trackEl = isDesktop ? trackRef.current : mobileTrackRef.current;
        const progressEl = isDesktop ? progressRef.current : mobileProgressRef.current;
        const glowEl = isDesktop ? glowRef.current : null;

        const applyPathGeometry = () => {
            if (!trackEl || !progressEl) return 0;

            const d = isDesktop
                ? buildJourneyPath(section, markers)
                : buildMobilePath(section, steps, 12);

            if (!d) return 0;

            trackEl.setAttribute("d", d);
            progressEl.setAttribute("d", d);
            if (glowEl) glowEl.setAttribute("d", d);

            const length = progressEl.getTotalLength();

            gsap.set(trackEl, {
                ...TRACK_STYLE,
                strokeWidth: isDesktop ? metrics.strokeWidth : 2,
                strokeDasharray: isDesktop ? "0.5 18" : "none",
                opacity: isDesktop ? 0.55 : 0.45,
            });
            gsap.set(progressEl, {
                ...PATH_STYLE,
                strokeWidth: isDesktop ? metrics.strokeWidth : 2,
                strokeDasharray: length,
                strokeDashoffset: length,
                opacity: isDesktop ? 0.42 : 0.38,
            });
            if (glowEl) {
                gsap.set(glowEl, {
                    stroke: "#FDB927",
                    strokeWidth: metrics.glowWidth,
                    strokeLinecap: "round",
                    fill: "none",
                    strokeDasharray: length,
                    strokeDashoffset: length,
                    opacity: 0.42 * 0.22,
                });
            }

            const anchors = isDesktop
                ? markers.map((marker) => markerPoint(section, marker))
                : steps.map((step) => stepAnchorPoint(section, step, 12));

            thresholdsRef.current = anchors.map((point) => pathProgressForPoint(progressEl, point));
            thresholdsRef.current[0] = 0;

            return length;
        };

        const setProgress = (progress) => {
            const length = progressEl.getTotalLength();
            const offset = length * (1 - progress);
            gsap.set(progressEl, { strokeDashoffset: offset });
            if (glowEl) gsap.set(glowEl, { strokeDashoffset: offset });
        };

        const syncStepStates = (progress, allowSparkle) => {
            const thresholds = thresholdsRef.current;
            if (!thresholds.length) return;

            let activeIndex = 0;
            for (let i = thresholds.length - 1; i >= 0; i -= 1) {
                if (progress >= thresholds[i] - 0.012) {
                    activeIndex = i;
                    break;
                }
            }

            if (activeIndex !== lastActiveRef.current) {
                if (allowSparkle && activeIndex > lastActiveRef.current && activeIndex > 0) {
                    const marker = markers[activeIndex];
                    if (marker && !sparkledRef.current.has(activeIndex)) {
                        triggerSparkle(marker);
                        sparkledRef.current.add(activeIndex);
                    }
                }
                lastActiveRef.current = activeIndex;
            }

            markers.forEach((marker, index) => {
                let state = "upcoming";
                if (index < activeIndex) state = "completed";
                else if (index === activeIndex) state = "current";
                applyMarkerState(marker, state);
            });

            steps.forEach((stepEl, index) => applyStepEmphasis(stepEl, index, activeIndex));
        };

        const initMarkerDefaults = () => {
            markers.forEach((marker, index) => {
                applyMarkerState(marker, index === 0 ? "current" : "upcoming");
            });
            steps.forEach((stepEl, index) => applyStepEmphasis(stepEl, index, 0));
        };

        if (reduceMotion) {
            gsap.set(heading, { opacity: 1, y: 0 });
            gsap.set(steps, { opacity: 1, y: 0 });
            const length = applyPathGeometry();
            if (length) {
                setProgress(1);
                markers.forEach((marker) => applyMarkerState(marker, "completed"));
                steps.forEach((stepEl) => gsap.set(stepEl, { opacity: 1 }));
            }
            return undefined;
        }

        gsap.set(heading, { opacity: 0, y: 18 });
        gsap.set(steps, { opacity: 0, y: isMobile ? 14 : 18 });
        initMarkerDefaults();

        const pathLength = applyPathGeometry();
        if (pathLength) setProgress(0);

        const ctx = gsap.context(() => {
            gsap.timeline({
                delay: 0.45,
                scrollTrigger: {
                    trigger: section,
                    start: "top 78%",
                    toggleActions: "play none none none",
                },
            })
                .to(heading, {
                    opacity: 1,
                    y: 0,
                    duration: 0.58,
                    ease: "power3.out",
                })
                .to(
                    steps,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.55,
                        stagger: isMobile ? 0.1 : 0.14,
                        ease: "power3.out",
                    },
                    "-=0.22",
                );

            if (pathLength > 0 && progressEl) {
                const progressTargets = glowEl ? [progressEl, glowEl] : [progressEl];

                progressTriggerRef.current = gsap.fromTo(
                    progressTargets,
                    { strokeDashoffset: () => progressEl.getTotalLength() },
                    {
                        strokeDashoffset: 0,
                        ease: "none",
                        scrollTrigger: {
                            trigger: stepsCol,
                            start: "top 72%",
                            end: "bottom 28%",
                            scrub: 0.35,
                            invalidateOnRefresh: true,
                            onUpdate: (self) => syncStepStates(self.progress, true),
                            onLeave: () => syncStepStates(1, false),
                            onLeaveBack: () => {
                                lastActiveRef.current = -1;
                                syncStepStates(0, false);
                            },
                        },
                    },
                ).scrollTrigger;
            }
        }, sectionRef);

        let resizeTimer = null;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const length = applyPathGeometry();
                if (!length || !progressTriggerRef.current) return;
                const progress = progressTriggerRef.current.progress;
                setProgress(progress);
                lastActiveRef.current = -1;
                syncStepStates(progress, false);
                ScrollTrigger.refresh();
            }, 250);
        };
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            clearTimeout(resizeTimer);
            progressTriggerRef.current = null;
            ctx.revert();
        };
    }, []);

    return (
        <section
            id="how-it-works"
            ref={sectionRef}
            data-testid="how-it-works-section"
            className="relative overflow-hidden bg-brand-surface py-24 sm:py-32"
        >
            <div className="bg-blueprint pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(70%_60%_at_30%_40%,black,transparent)]" />

            <svg
                className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full lg:block"
                aria-hidden="true"
                data-testid="process-journey-path"
            >
                <path ref={trackRef} />
                <path ref={glowRef} />
                <path ref={progressRef} strokeDasharray="0.5 18" />
            </svg>

            <svg
                className="pointer-events-none absolute inset-0 z-0 block h-full w-full lg:hidden"
                aria-hidden="true"
                data-testid="process-journey-path-mobile"
            >
                <path ref={mobileTrackRef} />
                <path ref={mobileProgressRef} />
            </svg>

            <div className="relative z-[1] mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <div className="lg:sticky lg:top-32">
                        <div ref={headingRef}>
                            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-brand-gold">
                                02 — Process
                            </p>
                            <h2 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter text-brand-deep sm:text-5xl">
                                Renovation <br />
                                <span className="italic text-brand-teal">Made Simple</span>
                            </h2>
                            <p className="mt-6 max-w-sm text-sm leading-relaxed text-brand-muted sm:text-base">
                                Four steps between the space you have and the space you want. The Genie walks you through
                                every one.
                            </p>
                            <button
                                type="button"
                                onClick={() => scrollToId("#cta")}
                                data-testid="process-cta-link"
                                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal transition-colors hover:text-brand-deep"
                            >
                                Start with step one
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                        </div>
                    </div>
                </div>

                <div ref={stepsColRef} className="lg:col-span-8">
                    {STEPS.map((step) => (
                        <div
                            key={step.num}
                            data-process-step
                            data-testid={`process-step-${step.num}`}
                            className="relative border-b border-brand-teal/10 py-12 pl-6 pr-20 sm:py-14 sm:pr-24 lg:pl-14 lg:pr-0"
                        >
                            <span
                                data-step-marker
                                className="absolute -left-[5px] top-14 hidden lg:block"
                            >
                                <span
                                    data-step-dot
                                    className="block h-2.5 w-2.5 rounded-full bg-brand-teal/20"
                                />
                                <svg
                                    data-step-sparkle
                                    viewBox="0 0 24 24"
                                    fill="#FDB927"
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -left-1 -top-1 h-3.5 w-3.5 opacity-0"
                                >
                                    <path d={SPARKLE_PATH} />
                                </svg>
                            </span>
                            <p className="font-mono text-sm font-semibold tracking-[0.2em] text-brand-gold">{step.num}</p>
                            <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight text-brand-deep sm:text-3xl">
                                {step.title}
                            </h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-brand-muted sm:text-base">{step.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
